// Ported from catalyst_tracker/static/tracker.js's "competitive read-through" + "Perspectives" engine
// (roughly its lines 207-250, 336-650, 820-848, 1041-1050, 1998-2039). Pure functions operating on a
// TrackerCatalyst[] passed explicitly, instead of closing over a global `rows` array the way the
// original does — callers pass the current `catalysts` list every time.
//
// Fidelity: the scoring math (weights, caps, thresholds) is transcribed operator-for-operator from the
// original; this is a hand-tuned heuristic model, not something to "clean up". Where the original built
// an HTML string (icons via `I()`, styled spans), this returns plain strings/structured data instead —
// TrackerIcon/CSS classes are applied by the calling component.
//
// Two deliberate scope cuts (named per call site below):
//   1. `regStart()` always returns "" — the original reads a trial's registry START date from a
//      Firestore doc (`meta/registry`, keyed by NCT) that has no Postgres/tracker_docs equivalent today
//      (our schema only carries `registry_pcd`/primary-completion, not start date). `rtPair()`'s
//      already-written fallback branch (`isPh3(x)` with no `st`) is exactly the degraded path this hits.
//   2. `pipeNames`/`pipeRows`/`inPipe` always resolve empty — the original cross-references a live
//      Firestore "pipeline" collection (per-company disclosed programs) that is architecturally present
//      in our schema (`tracker_docs` collection="pipeline") but has zero rows today (confirmed via a
//      live DB check: the worker that populates it has never run). `isMine`/`perspOf` are ported exactly
//      as written, so this starts working automatically the day that collection has rows — no code
//      change needed here.
//   Everything else (the class-signal/efficacy-bar scoring, same-mechanism/competing-setting detection,
//   the full `impactLines` competitive-reasoning text) runs on data that genuinely exists today
//   (histology, area, modality, moa, target, phase, date, outcome, signal, impact, pos) and is ported in
//   full.
//
// The live "look up this company" / "request a check" search-trigger flow (fireSearch/lookupCompany/
// openPerspFind/openPerspReq) is ported separately, in searchFlow.ts (shares this file's slugOf/cleanTerm
// re-exports) and PerspectivePanel.tsx/PerspectiveRequestModal.tsx — not here, since none of it is part
// of the scoring engine itself.
//
// Still NOT ported at all (named, genuinely separate features from Perspective, not required by
// anything this change wires up):
//   - `relatedOf`/`relHTML` (a separate "related programs" chip list shown elsewhere in the original UI)
//     and the whole "Benchmark" competitor-comparison table (`benchPeers`/`benchRow`, keyed off a
//     differently-shaped `r.eff` endpoint-data object) — both are separate features from Perspective.

import { TrackerCatalyst, TrackerCatalystOutcome, TrackerCatalystSignal } from '../../types';
import { assetKey, evPast, flagged } from './helpers';

// ---------- target / mechanism similarity (tracker.js:207-241) ----------

const EFFECTOR = new Set(['cd3', 'cd28', '4-1bb', 'hla-a*02:01']);

export function targetTokens(r: TrackerCatalyst): { toks: Set<string>; fam: Set<string> } {
  const raw = (r.target || '').replace(/\([^)]*\)/g, ' ');
  const toks = new Set<string>();
  const fam = new Set<string>();
  raw.split(/\s+x\s+|\s*\+\s*|;|,|\s\/\s/i).map((t) => t.trim().toLowerCase()).filter(Boolean).forEach((t0) => {
    let t = t0.replace(/-a$/, '').replace(/\s+/g, ' ').replace(/ multi-selective$/, '');
    if (t === 'androgen receptor') t = 'ar';
    if (t === 'cereblon') t = 'crbn';
    if (EFFECTOR.has(t)) return;
    toks.add(t);
    if (t === 'pd-1' || t === 'pd-l1') fam.add('pd-(l)1');
    if (/(^|k)ras\b/.test(t) || /^kras/.test(t)) fam.add('ras');
    if (/^cdk4/.test(t)) fam.add('cdk4');
    if (/^er$|^esr1/.test(t)) fam.add('er');
  });
  return { toks, fam };
}

export function similarity(a: TrackerCatalyst, b: TrackerCatalyst): number {
  if (assetKey(a) === assetKey(b)) return 0;
  let s = 0;
  if (a.moa && b.moa && a.moa.toLowerCase() === b.moa.toLowerCase()) s += 3;
  const A = targetTokens(a);
  const B = targetTokens(b);
  for (const t of A.toks) if (B.toks.has(t)) { s += 2; break; }
  if (!s) for (const f of A.fam) if (B.fam.has(f)) { s += 1; break; }
  return s;
}

/** modality, normalized; inferred from the mechanism text when the field is blank (tracker.js:362-373) */
export function modOf(r: TrackerCatalyst): string {
  const m = `${r.modality || ''} ${r.moa || ''}`.toLowerCase();
  if (/\badc\b|antibody[- ]drug conjugate|deruxtecan|vedotin|govitecan|pelitecan|conjugate/.test(m)) return 'adc';
  if (/radioligand|radiopharm|lutetium|actinium|\b177|225ac|212pb/.test(m)) return 'rlt';
  if (/car[- ]?t|cell therapy|til\b|tcr-t/.test(m)) return 'cell';
  if (/bispecific|trispecific|engager|x cd3/.test(m)) return 'bsab';
  if (/degrader|protac|glue/.test(m)) return 'degrader';
  if (/vaccine|mrna/.test(m)) return 'vaccine';
  if (/\bmab\b|antibody/.test(m)) return 'mab';
  if (/small molecule|inhibitor|tki|serd|\bib\b/.test(m)) return 'sm';
  return (r.modality || '').toLowerCase() || '?';
}

/** target -> number of distinct tracked drugs hitting it, cached per `rows` array identity (tracker.js:355-360) */
const classSizeCache = new WeakMap<TrackerCatalyst[], Map<string, number>>();
function classMap(rows: TrackerCatalyst[]): Map<string, number> {
  let m = classSizeCache.get(rows);
  if (m) return m;
  m = new Map();
  const seen = new Set<string>();
  for (const x of rows) {
    for (const t of targetTokens(x).toks) {
      const k = `${t}#${assetKey(x)}`;
      if (seen.has(k)) continue;
      seen.add(k);
      m.set(t, (m.get(t) || 0) + 1);
    }
  }
  classSizeCache.set(rows, m);
  return m;
}
export function classSize(rows: TrackerCatalyst[], r: TrackerCatalyst): number {
  const m = classMap(rows);
  const ts = [...targetTokens(r).toks];
  return ts.length ? Math.max(...ts.map((t) => m.get(t) || 1)) : 1;
}

// ---------- readout / phase helpers (tracker.js:338, 348-354, 419, 531) ----------

export const isReadoutRow = (r: TrackerCatalyst): boolean =>
  r.status === 'past' && ['positive', 'approved', 'negative', 'CRL', 'mixed'].includes(r.outcome || '') &&
  /topline|conference|regulatory/i.test(r.type || '');

export const isReadoutType = (r: TrackerCatalyst): boolean => /topline|conference|regulatory|filing/i.test(r.type || '');

export const isPh3 = (r: TrackerCatalyst): boolean => /3|registr|pivotal/i.test(r.phase || '');

/** [weight, label] — how strong a signal this event's result is for its mechanism (tracker.js:350) */
export function phaseW(r: TrackerCatalyst): [number, string] {
  if (/regulatory/i.test(r.type || '')) return [1, 'regulatory'];
  if (/3|registr|pivotal/i.test(r.phase || '')) return [1, 'Ph3'];
  if (/1\s*\/\s*2|1b\/2/i.test(r.phase || '')) return [0.55, 'Ph1/2'];
  if (/2/.test(r.phase || '')) return [0.7, 'Ph2'];
  if (/1/.test(r.phase || '')) return [0.4, 'Ph1'];
  return [0.6, r.phase || 'phase n/r'];
}

/** does this readout move the efficacy bar? approvals, and positive Phase 3 flagged practice-changing/new-indication (tracker.js:353) */
export const setsBar = (r: TrackerCatalyst): boolean =>
  r.outcome === 'approved' || (r.outcome === 'positive' && isPh3(r) && (['Practice-changing', 'New indication'] as TrackerCatalystSignal[]).includes(r.signal as TrackerCatalystSignal));

export const RT_CLASS_RES = (r: TrackerCatalyst): number =>
  r.outcome === 'approved' || (r.outcome === 'positive' && r.signal !== 'Proof of concept') ? 1
    : r.outcome === 'positive' ? 0.6
    : r.outcome === 'negative' ? -1
    : r.outcome === 'mixed' ? -0.3
    : 0;

export const RT_RES_TXT = (r: TrackerCatalyst): string =>
  r.outcome === 'approved' ? 'approval'
    : r.outcome === 'positive' ? (r.signal ? `positive (${r.signal.toLowerCase()})` : 'positive')
    : r.outcome === 'CRL' ? 'CRL'
    : r.outcome || '';

/**
 * NOT PORTED (scope cut 1, see file header): trial start date, read in the original from a Firestore
 * NCT->{s,p,st} registry cache (`meta/registry`) that has no Postgres equivalent — our schema only has
 * `registry_pcd` (primary completion), not start date. Always "", which is a value the original code
 * already handles (`rtPair`'s `isPh3(x)` branch has a documented fallback for when `st` is empty).
 */
export const regStart = (_x: TrackerCatalyst): string => '';

const drugNames = (x: TrackerCatalyst): Set<string> =>
  new Set(`${x.asset || ''} / ${x.generic || ''}`.toLowerCase().split(/[()\/,]| \+ /).map((t) => t.trim())
    .filter((t) => t.length >= 4 && !/^(sc|iv|monotherapy|combination)$/.test(t)));

export const rtSameDrug = (a: TrackerCatalyst, b: TrackerCatalyst): boolean => {
  const A = drugNames(a);
  for (const t of drugNames(b)) if (A.has(t)) return true;
  return false;
};

/**
 * A trial's registry recruitment status (tracker.js:604). The original's primary source is a
 * Firestore NCT->status cache (`REG[nct].st`) that has no Postgres equivalent (same gap as
 * `regStart` above) — but its fallback, `x.registry_status`, IS a real column (confirmed populated
 * on 673/950 live rows) and is ported here unchanged, so this degrades gracefully rather than
 * always resolving empty like `regStart` does.
 */
const regSt = (x: TrackerCatalyst): string => x.registry_status || '';

/** Is this trial actively recruiting (or not yet started)? (tracker.js:605) */
const enrolling = (x: TrackerCatalyst): boolean => /RECRUITING|NOT_YET|ENROLLING/.test(regSt(x)) || /trial initiation/i.test(x.type || '');

// ---------- line of therapy / drivers (tracker.js:1041-1050, 2000-2039) ----------

export const LINE_LABEL: Record<string, string> = {
  '1L': '1L', '2L+': '2L+ / relapsed', adjuvant: 'Adjuvant', periop: 'Neoadjuvant / periop',
  maint: 'Maintenance', na: 'Not applicable', nr: 'Not reported',
};

function settingOf(r: TrackerCatalyst): { line: string; bm: Set<string> } {
  const s = ` ${r.indication || ''} ${r.trial || ''} ${r.event || ''} `.toLowerCase();
  let line = '';
  if (/\badjuvant\b/.test(s) && !/neoadjuvant/.test(s)) line = 'adjuvant';
  else if (/neoadjuvant|perioperative|peri-operative|(?<!un)resectable/.test(s)) line = 'periop';
  else if (/maintenance/.test(s)) line = 'maint';
  else if (/\b1l\b|first[- ]line|frontline|front-line|previously untreated|treatment[- ]na[iï]ve|untreated|newly diagnosed|\bndmm\b|\btn\b/.test(s)) line = '1L';
  else if (/\b[2-9]l\b|\b[2-9]l\+|second[- ]line|third[- ]line|later?[- ]line|r\/r|\brr(mm|dlbcl|fl|cll|aml)\b|relapsed|refractory|previously treated|pretreated|pre-treated|post-|after |resistant|progress/.test(s)) line = '2L+';
  const bm = new Set<string>();
  const T: Array<[string, RegExp]> = [
    ['her2+', /her2[+-]?\s*(positive|\+)|her2\+/], ['her2-low', /her2[- ]low/], ['hr+', /hr\+|er\+|hormone receptor/],
    ['tnbc', /tnbc|triple[- ]negative/], ['egfr', /egfr/], ['alk', /\balk\b/], ['ros1', /ros1/], ['kras g12c', /g12c/],
    ['kras g12d', /g12d/], ['braf', /braf/], ['pd-l1 high', /pd-l1[^,;]*(≥|>=|>)\s*50|tps\s*(≥|>=)\s*50/],
    ['msi-h', /msi-h|dmmr/], ['mss', /\bmss\b|pmmr|microsatellite stable/], ['non-gcb', /non-gcb/], ['gcb', /\bgcb\b/],
    ['brca', /brca|hrd/], ['cldn18.2', /cldn18/], ['fgfr', /fgfr/], ['met', /\bmet\b/],
    ['platinum-resistant', /platinum[- ]resistant|proc\b/], ['platinum-sensitive', /platinum[- ]sensitive|psoc\b/],
    ['cisplatin-ineligible', /cis(platin)?[- ]ineligible/], ['crpc', /crpc|castration[- ]resistant/],
    ['hspc', /hspc|hormone[- ]sensitive|castration[- ]sensitive/], ['squamous', /\bsquamous\b/],
    ['non-squamous', /non[- ]squamous/], ['small cell', /small[- ]cell/],
  ];
  for (const [k, re] of T) if (re.test(s)) bm.add(k);
  if (bm.has('non-squamous')) bm.delete('squamous');
  if (bm.has('non-gcb')) bm.delete('gcb');
  return { line, bm };
}

export function lineInfo(r: TrackerCatalyst): { code: string; inferred: boolean; why?: string } {
  const rLine = (r as Record<string, unknown>).line as string | undefined;
  if (rLine && LINE_LABEL[rLine]) return { code: rLine, inferred: false };
  const s = ` ${r.indication || ''} ${r.trial || ''} ${r.event || ''} `.toLowerCase();
  const ph = ` ${(r.phase || '').toLowerCase()} `;
  const st = /imaging|diagnos|\bpet\b/.test(s) ? '' : settingOf(r).line;
  if (st) return { code: st, inferred: false };
  if (/imaging|diagnos|\bpet\b|screening|prevention|polyposis|rights deal|licens|strategy update|formulation|undergoing allogeneic/.test(s)) return { code: 'na', inferred: false };
  if (/(jaki|ici|io|treatment|taxane|chemo|therapy)[- ]na[iï]ve|untreated|mcspc|mhspc|castration[- ]sensitive|hormone[- ]sensitive/.test(s)) return { code: '1L', inferred: true, why: 'therapy-naive / sensitive population' };
  if (/experienced|recurrent|platinum[- ]sensitive|\bprior\b|\bafter\b|biochemically/.test(s)) return { code: '2L+', inferred: true, why: 'recurrent or previously treated population' };
  if (/phase 1|phase i\b|ph ?1\b|first-in-human|dose[- ]escalation|basket/.test(ph + s) || /advanced solid tumou?rs/.test(s)) return { code: '2L+', inferred: true, why: 'early-phase trial in advanced disease, usually enrolls after standard therapy' };
  return { code: 'nr', inferred: false };
}

const DRIVERS: Array<[string, RegExp]> = [
  ['EGFR exon 20', /egfr[^,;]{0,12}(exon ?20|ex20)/], ['EGFR', /\begfr(m\b|\b)/], ['ALK', /\balk\b/], ['ROS1', /\bros1\b/],
  ['RET', /\bret\b/], ['NTRK', /\bntrk/], ['MET', /\bMET\b|METex14|c-MET/i], ['KRAS G12C', /g12c/], ['KRAS G12D', /g12d/],
  ['KRAS/RAS', /\bk?ras\b|\bras-mutant|\bras\(on\)/], ['BRAF', /\bbraf\b/], ['HER2-mutant', /her2[- ]?(mut|exon)|erbb2[- ]?mut/],
  ['HER2-low', /her2[- ]?(low|ultralow)/], ['HER2+', /her2[+-]?\s*(positive|\+)|her2\+|her2-amplified|her2 amp/],
  ['NRG1', /\bnrg1/], ['MTAP-deleted', /mtap/], ['FGFR2', /fgfr2/], ['FGFR3', /fgfr3/], ['IDH1', /idh1/], ['IDH2', /idh2/],
  ['FLT3', /flt3/], ['NPM1', /npm1/], ['KMT2A', /kmt2a|mll-r|mllr/], ['PIK3CA', /pik3ca|pi3k[α a]*[- ]mut/], ['ESR1', /esr1/],
  ['BRCA/HRD', /brca|\bhrd\b/], ['CLDN18.2', /cldn18|claudin ?18/], ['MSI-H/dMMR', /msi-h|dmmr/],
  ['HR+', /hr\+|er\+|hormone receptor[- ]positive/], ['TNBC', /tnbc|triple[- ]negative/], ['BCR-ABL', /bcr-abl|ph\+/],
  ['KIT', /\bkit\b/], ['TP53/del17p', /tp53|del\(?17p/],
];
export function driversOf(r: TrackerCatalyst): Set<string> {
  const raw = ` ${r.indication || ''} ${r.trial || ''} `;
  const txt = raw.toLowerCase();
  const out = new Set<string>();
  for (const [k, re] of DRIVERS) {
    const m = k === 'MET' ? raw.match(/\bMET\b|METex14|c-MET/) : txt.match(re);
    if (m && !/^[^,;]{0,18}?(not detected|wild[- ]?type|\bwt\b|negative|unselected)/.test(txt.slice(m.index! + m[0].length))) out.add(k);
  }
  if (!out.size && !/[/,+&;]|\band\b|\bx\b/i.test(r.target || '')) {
    const tg = ` ${r.target || ''} `.toLowerCase();
    for (const [k, re] of DRIVERS) if (re.test(tg) && !/^(HR\+|TNBC|HER2-low|HER2\+|BRCA\/HRD|MSI-H\/dMMR|TP53\/del17p)$/.test(k)) out.add(k);
  }
  if (out.has('EGFR exon 20')) out.delete('EGFR');
  if (out.has('KRAS G12C') || out.has('KRAS G12D')) out.delete('KRAS/RAS');
  if (out.has('HER2-mutant')) out.delete('HER2+');
  return out;
}

const coreDrv = (r: TrackerCatalyst): string => [...driversOf(r)].filter((k) => k !== 'TP53/del17p').sort().join('|');

const settingTxt = (r: TrackerCatalyst): string =>
  [r.histology, LINE_LABEL[lineInfo(r).code] && !['nr', 'na'].includes(lineInfo(r).code) ? LINE_LABEL[lineInfo(r).code] : '', [...driversOf(r)].join('/')]
    .filter(Boolean).join(' · ');

// ---------- pairwise read-through scoring (tracker.js:374-463) ----------

export interface RtPairResult { score: number; cls: number; bar: number; why: string; spec: number }

const sgn = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

/** The per-pair class-signal / efficacy-bar score: how much readout `r` moves pending event `x`'s odds. */
export function rtPair(rows: TrackerCatalyst[], r: TrackerCatalyst, x: TrackerCatalyst): RtPairResult | null {
  const sim = similarity(r, x);
  const sameTumor = !!r.histology && x.histology === r.histology;
  const L = lineInfo(r).code;
  const sameLine = sameTumor && !['nr', 'na'].includes(L) && lineInfo(x).code === L;
  const sameSetting = sameLine && coreDrv(x) === coreDrv(r);
  const [ev, evTxt] = phaseW(r);
  const parts: string[] = [];
  let cls = 0;
  let bar = 0;

  // 1. class signal: up to 15 points. Only between drugs of the same modality; only a "new" class
  // (<=8 tracked drugs on the target) tells us anything across different tumors.
  const res = RT_CLASS_RES(r);
  const n = classSize(rows, r);
  if (sim >= 2 && res && modOf(r) === modOf(x) && (sameTumor || (sim >= 3 && n <= 8))) {
    const sw = sim >= 5 ? 1 : sim >= 3 ? 0.8 : 0.5;
    const tw = (sameLine ? 1 : sameTumor ? 0.8 : 0.4 * (n <= 3 ? 1 : 0.6)) * (sameTumor && n > 8 ? 0.7 : 1);
    cls = 15 * res * sw * tw * ev;
    if (Math.abs(cls) >= 0.5) {
      parts.push(`${res > 0 ? 'Class validated' : 'Class setback'} (${sim >= 5 ? 'same mechanism and target' : sim >= 3 ? 'same mechanism' : 'same target'}, ${sameLine ? 'same tumor and line' : sameTumor ? 'same tumor' : 'other tumor, new class'}; ${evTxt} ${RT_RES_TXT(r)}) ${sgn(Math.round(cls))}`);
    }
  }

  // 2. efficacy bar: only if it moved before this event reads out, and not part of this event's own regimen.
  const inArm = [r.generic, r.asset].filter(Boolean).some((nm) => nm!.length > 3 && `${x.asset} ${x.event} ${x.indication}`.toLowerCase().includes(nm!.toLowerCase().split(/[ (+]/)[0]));
  if (sameSetting && setsBar(r) && !inArm && r.date && x.date && x.date > r.date) {
    const st = regStart(x);
    const mag = r.outcome === 'approved' || r.signal === 'Practice-changing' ? 1 : 0.7;
    let b: number;
    let why: string;
    if (/regulatory|filing/i.test(x.type || '')) { b = 3; why = 'regulators and payers will compare against the new standard'; }
    else if (isPh3(x) && st && st >= r.date) { b = 8; why = `trial started ${fmtDayShort(st)}, after the bar moved: control arm likely the new standard`; }
    else if (isPh3(x)) { b = 3; why = st ? `trial started ${fmtDayShort(st)}, before the bar moved: control arm is the old standard, but results will be read against the new one` : 'Phase 3 likely enrolled before the bar moved (old control arm), but results will be read against the new standard'; }
    else { b = 6; why = 'early/single-arm data will be judged against the new benchmark'; }
    bar = -b * mag;
    parts.push(`Bar raised ${fmtDayShort(r.date)} by ${RT_RES_TXT(r)}; ${why} ${sgn(Math.round(bar))}`);
  }

  const score = Math.round(cls + bar);
  const RT_MIN = 1;
  if (Math.abs(score) < RT_MIN) return null;
  if (parts.length === 1) parts[0] = parts[0].replace(/ [+-]\d+$/, '');
  return { score, cls, bar, why: parts.join(' · '), spec: sameSetting ? 3 : sameLine ? 2 : sameTumor ? 1 : 0 };
}

function fmtDayShort(iso: string): string {
  const [y, m, d] = iso.split('-');
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MON[+m - 1]} ${+d} '${y.slice(2)}`;
}

interface RtAllItem extends RtPairResult { x: TrackerCatalyst }

const rtAllCache = new WeakMap<TrackerCatalyst[], Map<string, RtAllItem[]>>();
/** Every pending event a given past readout's pair-score is nonzero for (tracker.js:409-416). */
function readThroughAll(rows: TrackerCatalyst[], r: TrackerCatalyst): RtAllItem[] {
  if (!isReadoutRow(r)) return [];
  let cache = rtAllCache.get(rows);
  if (!cache) { cache = new Map(); rtAllCache.set(rows, cache); }
  const hit = cache.get(r.id);
  if (hit) return hit;
  const out: RtAllItem[] = [];
  const same = (x: TrackerCatalyst) => assetKey(x) === assetKey(r) || !!(r.generic && x.generic && r.generic.toLowerCase() === x.generic.toLowerCase());
  for (const x of rows) {
    if (x.status !== 'upcoming' || same(x) || !/topline|conference|regulatory|filing/i.test(x.type || '')) continue;
    const p = rtPair(rows, r, x);
    if (p) out.push({ x, ...p });
  }
  cache.set(r.id, out);
  return out;
}

export interface ReadThroughItem { x: TrackerCatalyst; score: number; eff: number; why: string; spec: number }

const readThroughCache = new WeakMap<TrackerCatalyst[], Map<string, ReadThroughItem[]>>();
/**
 * `readThroughAll` deduped to one entry per asset (same-drug entries merged, keeping the pairing with
 * the strongest `spec`, then largest |score|, then earliest date) — used by the detail-dialog
 * read-through browsing panel (tracker.js:420-432).
 */
export function readThrough(rows: TrackerCatalyst[], r: TrackerCatalyst): ReadThroughItem[] {
  if (!isReadoutRow(r)) return [];
  let cache = readThroughCache.get(rows);
  if (!cache) { cache = new Map(); readThroughCache.set(rows, cache); }
  const hit = cache.get(r.id);
  if (hit) return hit;
  const byAsset = new Map<string, ReadThroughItem>();
  for (const { x, ...p } of readThroughAll(rows, r)) {
    let k = assetKey(x);
    for (const [kk, v] of byAsset) if (kk !== k && rtSameDrug(v.x, x)) { k = kk; break; }
    const prev = byAsset.get(k);
    const item: ReadThroughItem = { x, score: p.score, eff: Math.sign(p.score), why: p.why, spec: p.spec };
    if (!prev || item.spec > prev.spec
      || (item.spec === prev.spec && (Math.abs(item.score) > Math.abs(prev.score)
        || (Math.abs(item.score) === Math.abs(prev.score) && (x.date || '9') < (prev.x.date || '9'))))) {
      byAsset.set(k, item);
    }
  }
  const out = [...byAsset.values()].sort((a, b) => Math.abs(b.score) - Math.abs(a.score) || (a.x.date || '9').localeCompare(b.x.date || '9'));
  cache.set(r.id, out);
  return out;
}

interface RtIndexEntry { src: TrackerCatalyst; score: number; cls: number; bar: number; eff: number; why: string }

const rtIndexCache = new WeakMap<TrackerCatalyst[], Map<string, RtIndexEntry[]>>();
const RT_WINDOW = 365;

function addDaysIso(iso: string, d: number): string {
  const t = new Date(`${iso}T12:00:00`);
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 10);
}

/** id of upcoming catalyst -> every past readout (within the last RT_WINDOW days) that affects it (tracker.js:434-443). */
function rtIndex(rows: TrackerCatalyst[]): Map<string, RtIndexEntry[]> {
  const cached = rtIndexCache.get(rows);
  if (cached) return cached;
  const today = new Date().toISOString().slice(0, 10);
  const index = new Map<string, RtIndexEntry[]>();
  for (const r of rows) {
    if (!isReadoutRow(r) || !r.date || r.date < addDaysIso(today, -RT_WINDOW) || r.date > today) continue;
    for (const it of readThroughAll(rows, r)) {
      const k = it.x.id;
      if (!index.has(k)) index.set(k, []);
      const sk = (t: TrackerCatalyst) => (t.generic || '').toLowerCase() || assetKey(t);
      const L = index.get(k)!;
      const i = L.findIndex((t) => sk(t.src) === sk(r));
      const e: RtIndexEntry = { src: r, score: it.score, cls: it.cls, bar: it.bar, eff: Math.sign(it.score), why: it.why };
      if (i < 0) L.push(e); else if ((r.date || '') > (L[i].src.date || '')) L[i] = e;
    }
  }
  for (const L of index.values()) L.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  rtIndexCache.set(rows, index);
  return index;
}

const RT_CAP = 25;
/** combine: class signals with diminishing returns; the bar counts once, at its strongest (tracker.js:446-451). */
function rtNet(items: RtIndexEntry[]): number {
  const dim = (arr: number[]) => arr.sort((a, b) => Math.abs(b) - Math.abs(a)).reduce((s, v, i) => s + v * Math.pow(0.5, i), 0);
  const cl = items.map((t) => t.cls || 0);
  const bar = Math.min(0, ...items.map((t) => t.bar || 0));
  const tot = dim(cl.filter((v) => v > 0)) + dim(cl.filter((v) => v < 0)) + bar;
  return Math.max(-RT_CAP, Math.min(RT_CAP, Math.round(tot)));
}

export interface PosAdj { base: number | null; adj: number | null; net: number; items: RtIndexEntry[] }

/** a pending event's P(positive): the stored base estimate, adjusted by related readouts (tracker.js:841-846). */
export function posAdj(rows: TrackerCatalyst[], r: TrackerCatalyst): PosAdj | null {
  if (r.status !== 'upcoming') return null;
  const pos = (r as Record<string, unknown>).pos as number | null | undefined;
  const items = rtIndex(rows).get(r.id) || [];
  const net = items.length ? rtNet(items) : 0;
  if (pos == null && !net) return null;
  const adj = pos == null ? null : Math.max(1, Math.min(99, pos + net));
  return { base: pos ?? null, adj, net, items };
}

export const posVal = (rows: TrackerCatalyst[], r: TrackerCatalyst): number | null => posAdj(rows, r)?.adj ?? null;

// ---------- Perspectives: view every catalyst as one company (tracker.js:491-647) ----------

/**
 * NOT PORTED (scope cut 2, see file header): the original's `PIPE` map comes from a live Firestore
 * "pipeline" collection (per-company disclosed programs beyond tracked catalysts). Our schema has the
 * same concept architecturally (`tracker_docs` collection="pipeline") but it has zero rows today — the
 * worker that would populate it has never run (confirmed via a live DB check). `isMine`/`perspOf` below
 * are ported exactly as written against this always-empty source, so partnership/pipeline cross-
 * referencing will start working automatically the day that collection is populated.
 */
function pipeRows(_company: string): TrackerCatalyst[] { return []; }
function inPipe(_r: TrackerCatalyst, _company: string): boolean { return false; }

export const isMine = (r: TrackerCatalyst, company: string): boolean =>
  r.company === company || (Array.isArray(r.partners) && r.partners.includes(company)) || (!(r as Record<string, unknown>).pipeline && inPipe(r, company));

/** does this event's regimen include one of company C's drugs (e.g. "+ atezolizumab")? (tracker.js:525-530) */
function usesMyDrug(rows: TrackerCatalyst[], e: TrackerCatalyst, company: string): string {
  const txt = ` ${e.asset || ''} ${e.indication || ''} ${e.event || ''} `.toLowerCase();
  const names = new Set(
    rows.filter((x) => isMine(x, company) && assetKey(x) !== assetKey(e)).concat(pipeRows(company).filter((x) => !rtSameDrug(x, e)))
      .map((x) => (x.generic || x.asset || '').toLowerCase().split(/[ (+]/)[0]).filter((n) => n.length >= 6),
  );
  for (const n of names) {
    if (n && new RegExp(`\\b${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(txt)) return n;
  }
  return '';
}

/** how two catalysts are related, without direction (tracker.js:533-544):
 * 4 = one is a component of the other's combination regimen (partner, not competitor)
 * 3 = same tumor, line and population  2 = same mechanism/target, same tumor  1 = same mechanism, other tumor (newer class) */
export function relLevel(rows: TrackerCatalyst[], a: TrackerCatalyst, b: TrackerCatalyst): number {
  if (assetKey(a) === assetKey(b)) return 0;
  const inName = (x: TrackerCatalyst, y: TrackerCatalyst) => [...drugNames(x)].some((n) => n.length >= 5 && (y.asset || '').toLowerCase().includes(n));
  if ((/\+/.test(b.asset || '') && inName(a, b)) || (/\+/.test(a.asset || '') && inName(b, a))) return 4;
  if (rtSameDrug(a, b)) return 0;
  const sameTumor = !!a.histology && b.histology === a.histology;
  const sim = similarity(a, b);
  const L = lineInfo(a).code;
  if (sameTumor && !['nr', 'na'].includes(L) && lineInfo(b).code === L && coreDrv(b) === coreDrv(a)) return 3;
  if (sim >= 2 && sameTumor) return 2;
  if (sim >= 3 && classSize(rows, a) <= 8) return 1;
  return 0;
}

const REL_TXT: Record<string, string> = {
  '1.5': 'is in the same tumor and population as', '4': 'is part of your combination regimen',
  '3': 'competes in your setting', '2': 'same mechanism or target as your drug, same tumor', '1': 'same mechanism as your drug, other tumor',
};

/** hypothetical result of an upcoming event, used to evaluate "if positive" / "if negative" scenarios (tracker.js:548-552) */
function hypo(e: TrackerCatalyst, outcome: 'positive' | 'negative'): TrackerCatalyst {
  const reg = /regulatory|filing/i.test(e.type || '');
  const o: TrackerCatalystOutcome = reg ? (outcome === 'positive' ? 'approved' : 'CRL') : outcome;
  const sig = outcome === 'positive' ? (e.signal || (isPh3(e) ? 'Practice-changing' : 'Proof of concept')) : e.signal;
  return { ...e, status: 'past', outcome: o, signal: sig as TrackerCatalystSignal, type: reg ? 'Regulatory decision' : (e.type || 'Topline') };
}

const LINE_RANK: Record<string, number> = { periop: 0, adjuvant: 0, '1L': 1, maint: 1.5, '2L+': 2 };

interface RelEntry { k: string; x: TrackerCatalyst; l: number }

/** what a result means for your drugs, by dimension — the actual competitive-reasoning text (tracker.js:607-641). */
function impactLines(e: TrackerCatalyst, rel: RelEntry[], oc: 'positive' | 'negative', usesYours: string, past?: boolean): string[] {
  const out: string[] = [];
  const pos = oc === 'positive';
  const h = hypo(e, oc);
  const bar = pos && (setsBar(h) || /regulatory/i.test(h.type || ''));
  const will = past ? '' : 'would ';
  const comp = rel.filter((t) => t.l === 3);
  const cls = rel.filter((t) => t.l === 2 || t.l === 1);
  const part = rel.find((t) => t.l === 4);
  const other = rel.filter((t) => t.l === 1.5);
  const tx = (t: RelEntry) => `your ${t.x.asset}${t.x.trial && t.x.trial.length < 28 ? ` (${t.x.trial})` : ''}`;

  if (usesYours) out.push(pos ? `Commercial: supportive. More use of your ${usesYours} as its combination partner.` : `Commercial: a setback for a regimen built on your ${usesYours}.`);
  if (part) out.push(pos ? `Commercial: supportive for ${tx(part)}, which includes ${e.asset}.` : `Commercial: a setback for a component of ${tx(part)}.`);

  for (const t of comp.slice(0, 2)) {
    const x = t.x;
    const later = x.status === 'upcoming' && !!x.date && !!e.date && x.date > e.date;
    if (pos) {
      if (later && bar) out.push(`Efficacy bar: ${tx(t)} ${will}be judged against ${e.asset}'s result, not today's standard of care.`);
      if (x.status === 'upcoming' && isPh3(x) && enrolling(x) && bar) out.push(`Trial timeline: ${tx(t)} is still enrolling against the old comparator; investigators and IRBs may push for a protocol amendment (new control arm or crossover), which can slow accrual and delay readout.`);
      if (enrolling(x) || enrolling(e)) out.push(`Enrollment and sites: ${e.asset} competes for the same patients and sites in ${settingTxt(x)}; positive data makes its trials and, once approved, off-trial use more attractive, so screening for ${tx(t)} may slow.`);
      if (/regulatory|filing/i.test(x.type || '') || (x.status === 'upcoming' && !isPh3(x))) out.push(`Regulatory: once an effective therapy is available in this setting, accelerated approval on single-arm or early data gets harder; regulators may expect a randomized comparison against ${e.asset}.`);
      if (x.status === 'upcoming' && isPh3(x)) out.push(`OS endpoint: if patients in ${tx(t)} can receive ${e.asset} after progression, overall survival separation may be diluted; PFS becomes more important for the label.`);
      out.push(`Commercial: ${e.asset} ${will}become a stronger competitor to ${tx(t)} in ${settingTxt(x)}.`);
    } else {
      out.push(`Commercial: one fewer credible competitor to ${tx(t)} in ${settingTxt(x)}.`);
      if (enrolling(x)) out.push(`Enrollment and sites: patients and sites in ${settingTxt(x)} may free up for ${tx(t)}.`);
    }
  }
  for (const t of cls.slice(0, 2)) {
    const same = t.x.histology === e.histology;
    out.push(pos
      ? `Class biology: ${will}support the mechanism behind ${tx(t)}${same ? '' : ` (read across from ${e.histology || 'another tumor'})`}; investigators and regulators tend to be more receptive.`
      : `Class biology: a setback for the mechanism behind ${tx(t)}; expect questions on target validity and any shared safety signal${same ? '' : `, though it is a different tumor (${e.histology || 'n/r'})`}.`);
  }
  for (const t of other.slice(0, 2)) {
    const rx = LINE_RANK[lineInfo(t.x).code];
    const re = LINE_RANK[lineInfo(e).code];
    if (rx == null || re == null || !pos) continue;
    if (re > rx) out.push(`OS endpoint: ${e.asset} ${will}add an effective later-line option in ${e.histology}; subsequent use after ${tx(t)} can dilute its overall survival comparison.`);
    else out.push(`Patient population: ${e.asset} moving into an earlier line means patients reaching ${tx(t)} will increasingly be pre-treated with it, changing eligibility and expected benefit.`);
  }
  if (!out.length) out.push(pos ? 'No direct effect on your pending programs beyond the read-across above.' : 'No direct effect on your pending programs.');
  return [...new Set(out)].slice(0, 7);
}

export interface PerspScenario { oc: 'positive' | 'negative'; p: number | null; lines: string[] }
export interface PerspAnnotation {
  kind: 'own' | 'future' | 'past';
  title: string;
  lines: string[];
  scen: PerspScenario[] | null;
  weight: number;
}

const perspCache = new WeakMap<TrackerCatalyst[], Map<string, Map<string, PerspAnnotation | null>>>();

/** Everything "viewing as company C" has to say about event `e` — direct ownership, competitive
 * exposure for an upcoming event, or how a past readout affected you (tracker.js:554-601). */
export function perspOf(rows: TrackerCatalyst[], e: TrackerCatalyst, company: string): PerspAnnotation | null {
  if (!company) return null;
  let byCompany = perspCache.get(rows);
  if (!byCompany) { byCompany = new Map(); perspCache.set(rows, byCompany); }
  let cache = byCompany.get(company);
  if (!cache) { cache = new Map(); byCompany.set(company, cache); }
  if (cache.has(e.id)) return cache.get(e.id) ?? null;

  const live = new Set(rows.filter((x) => isMine(x, company) && (x.status === 'upcoming' || x.outcome === 'approved')).map(assetKey));
  const mine0 = rows.filter((x) => isMine(x, company) && live.has(assetKey(x)));
  const mine = mine0.concat(pipeRows(company).filter((p) => !mine0.some((x) => rtSameDrug(x, p) && x.histology === p.histology)));

  let out: PerspAnnotation | null = null;

  if (isMine(e, company)) {
    const lines: string[] = [];
    if (e.company !== company) lines.push(`Partnered: ${e.company} drug; ${company} is ${((e.partner_role as Record<string, string> | undefined) || {})[company] || 'a partner'}.`);
    if (e.status === 'upcoming' && isReadoutType(e)) {
      const pv = posVal(rows, e);
      const comp = new Set(rows.filter((x) => !isMine(x, company) && relLevel(rows, e, x) === 3).map((x) => x.company));
      const partners = new Set(rows.filter((x) => !isMine(x, company) && relLevel(rows, e, x) === 4).map((x) => x.asset));
      if (pv != null) lines.push(`Chance of a positive result: ${pv}%.`);
      if (partners.size) lines.push(`Partner regimens that include it: ${[...partners].slice(0, 4).join(', ')}.`);
      if (comp.size) lines.push(`${comp.size} compan${comp.size === 1 ? 'y competes' : 'ies compete'} in this setting (${settingTxt(e)}): ${[...comp].slice(0, 6).join(', ')}${comp.size > 6 ? '…' : ''}.`);
    }
    out = { kind: 'own', title: e.company === company ? 'Your catalyst' : 'Your partnered catalyst', lines, scen: null, weight: 100 };
  } else if (isReadoutType(e) || isReadoutRow(e)) {
    const rel: RelEntry[] = [];
    for (const x of mine) {
      const l = relLevel(rows, e, x);
      if (!l) continue;
      const k = assetKey(x);
      const prev = rel.find((t) => t.k === k);
      if (!prev) rel.push({ k, x, l });
      else if (l > prev.l || (l === prev.l && x.status === 'upcoming' && prev.x.status !== 'upcoming')) { prev.x = x; prev.l = l; }
    }
    for (const x of mine) {
      if (rel.some((t) => t.k === assetKey(x))) continue;
      if (x.status !== 'upcoming' || !isReadoutType(x)) continue;
      if (e.histology && x.histology === e.histology && coreDrv(x) === coreDrv(e) && !['nr', 'na'].includes(lineInfo(x).code) && !['nr', 'na'].includes(lineInfo(e).code) && lineInfo(x).code !== lineInfo(e).code) {
        rel.push({ k: assetKey(x), x, l: 1.5 });
      }
    }
    const usesYours = usesMyDrug(rows, e, company);
    if (rel.length || usesYours) {
      rel.sort((a, b) => b.l - a.l);
      const head: string[] = [];
      if (usesYours) head.push(`Its regimen uses your ${usesYours}.`);
      for (const L of [4, 3, 2, 1.5, 1]) {
        const g = rel.filter((t) => t.l === L);
        if (!g.length) continue;
        const names = [...new Set(g.map((t) => t.x.pipeline
          ? `${t.x.asset} (${t.x.marketed ? `marketed in ${t.x.histology || 'this tumor'}` : t.x.completed ? `${t.x.phase} completed, ${t.x.histology || 'n/r'}` : `${[t.x.phase, t.x.histology].filter(Boolean).join(', ') || 'pipeline'}; no tracked catalyst`})`
          : t.x.asset))].slice(0, 4).join(', ');
        head.push(`${e.asset} ${REL_TXT[String(L)]}: your ${names}.`);
      }
      if (e.status === 'upcoming') {
        const pv = posVal(rows, e);
        const scen: PerspScenario[] = (['positive', 'negative'] as const).map((oc) => ({
          oc, p: pv == null ? null : (oc === 'positive' ? pv : 100 - pv), lines: impactLines(e, rel, oc, usesYours),
        }));
        const first = rel.filter((t) => t.l === 3 && t.x.status === 'upcoming' && isReadoutType(t.x) && t.x.date && e.date && t.x.date <= e.date!);
        if (first.length) head.push(`Your ${first.map((t) => `${t.x.asset} (${t.x.window || t.x.date})`).join(', ')} read${first.length === 1 ? 's' : ''} out first, so it is judged against today's bar, not one this result could set.`);
        out = { kind: 'future', title: 'Could affect you', lines: head, scen, weight: 30 + (rel.length ? rel[0].l * 8 : 20) + (e.impact === 'High' ? 10 : 0) };
      } else {
        const oc: 'positive' | 'negative' | '' = ['positive', 'approved'].includes(e.outcome || '') ? 'positive' : ['negative', 'CRL'].includes(e.outcome || '') ? 'negative' : '';
        const lines = head.concat(oc ? impactLines(e, rel, oc, usesYours, true) : []);
        const today = new Date().toISOString().slice(0, 10);
        const recent = !!e.date && e.date >= addDaysIso(today, -RT_WINDOW);
        out = { kind: 'past', title: oc ? 'Affected you' : 'Related to you', lines, scen: null, weight: (recent ? 40 : 10) + (rel[0] ? rel[0].l * 3 : 0) };
      }
    }
  }

  cache.set(e.id, out);
  return out;
}

export interface PerspSummary { own: number; pastAffected: number; futureCould: number; watch: TrackerCatalyst[] }

/** The "Viewing as X" bar's summary counts (tracker.js:675-680's own/past/fut/top). */
export function perspSummary(rows: TrackerCatalyst[], company: string): PerspSummary {
  const today = new Date().toISOString().slice(0, 10);
  const all = rows.map((r) => ({ r, p: perspOf(rows, r, company) })).filter((t) => t.p);
  const own = all.filter((t) => t.p!.kind === 'own' && t.r.status === 'upcoming').length;
  const fut = all.filter((t) => t.p!.kind === 'future').sort((a, b) => b.p!.weight - a.p!.weight);
  const past = all.filter((t) => t.p!.kind === 'past' && t.r.date && t.r.date >= addDaysIso(today, -RT_WINDOW));
  const watch = fut.filter((t) => t.r.date && t.r.date <= addDaysIso(today, 180)).slice(0, 3).map((t) => t.r);
  return { own, pastAffected: past.length, futureCould: fut.length, watch };
}

// Re-exported so callers that only need relevance filtering don't have to import `flagged`/`evPast`
// from two different modules.
export { evPast, flagged };
